import { useEffect, useState } from 'react';
import { httpApiSmartAuditSilent } from '@app/smart-audit/api/silentClient';
import { readToken } from '@app/services/localStorage.service';
import type { Envelope, Me } from '@app/smart-audit/api/types';

/**
 * `GET /me` without the shared interceptors (D9): the check must stay silent on the module picker, so a network error
 * or a 5xx shows no toast and a 401 does not sign the user out of the whole app.
 */
const fetchMeSilently = async (): Promise<Me> => {
  const token = readToken();
  const res = await httpApiSmartAuditSilent.get<Envelope<Me>>('/me', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.data.data;
};

/**
 * Whether the module-selection screen shows the Smart Audit tile. It is shown until `/me` proves the account holds no
 * `5s/` permission (and is not root); a failing request keeps it shown, because the module reports its own errors.
 * Plain state instead of react-query: the host layout sits outside the module's QueryClientProvider.
 *
 * `enabled` false makes no request, so other routes and Kaizen-only accounts do not call the backend for nothing.
 */
export const useSmartAuditVisible = (enabled = true): boolean => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    fetchMeSilently()
      .then((me) => {
        if (active && !me.is_root && !me.permissions.some((p) => p.startsWith('5s/'))) setVisible(false);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [enabled]);

  return visible;
};
